'use client';

/**
 * InfoPopup — 정보 팝업 컴포넌트. (상품 페이지)
 *
 * PC: 화면 중앙 모달 / 모바일: 하단 시트(bottom sheet) 형태로 자동 전환.
 * 오버레이 클릭 및 닫기 버튼은 onClose에 연결됨.
 * body 스크롤 잠금, ESC 닫기, focus trap, 포커스 이동/복귀는 CommonPopupProvider가 처리함.
 *
 * [PopupProps]
 * - `isOpen`: 팝업 열림 여부 (필수)
 * - `onClose`: 닫기 핸들러 (필수) — 오버레이·닫기 버튼에 연결됨. ESC 닫기는 CommonPopupProvider가 처리
 * - `content`: 팝업 본문 (필수) — ReactNode
 * - `title`: 헤더 제목 텍스트
 * - `tag`: 헤더 태그 텍스트 (Badge 컴포넌트로 렌더)
 * - `tagBgColor`: 태그 배경색 (CSS 색상값, 예: "#FF5C00")
 * - `note`: 본문 하단 소주석 텍스트 (작은 회색 글씨)
 * - `footer.text`: 하단 링크 텍스트
 * - `footer.href`: 하단 링크 URL
 * - `className`: 팝업 박스(popup_box)에 추가될 클래스명
 *
 * [PopupData]
 * 팝업 내용을 데이터로 분리할 때 사용하는 타입.
 * isOpen / onClose는 제외 — openLayer에서 close를 받아 주입.
 *
 * [사용 패턴 — CommonPopupProvider 하위에서 openLayer로 열기]
 * ```tsx
 * const { openLayer } = useCommonPopup();
 *
 * openLayer('my-popup', close => (
 *   <Popup
 *     isOpen
 *     onClose={close}
 *     title="팝업 제목"
 *     tag="추천"
 *     tagBgColor="#FF5C00"
 *     content={<p>팝업 본문 내용입니다.</p>}
 *     note="* 유의사항 텍스트"
 *     footer={{ text: "자세히 알아보기", href: "/detail" }}
 *   />
 * ));
 * ```
 *
 * [데이터 분리 패턴 — data.tsx + openLayer]
 * ```tsx
 * // data.tsx
 * export const myPopupData: PopupData = {
 *   title: "팝업 제목",
 *   tag: "추천",
 *   tagBgColor: "#FF5C00",
 *   content: <p>본문</p>,
 *   note: "* 유의사항",
 *   footer: { text: "자세히 알아보기", href: "/detail" },
 * };
 *
 * openLayer('my-popup', close => <Popup isOpen onClose={close} {...myPopupData} />);
 * ```
 */

import { useId } from 'react';
import s from './InfoPopup.module.scss';
import Icon from '../ui/Icon';
import { ActionCloseType1, ArrowArrow } from '../ui/SvgSprite';
import { Button } from '../ui/Button';
import Badge from '../ui/Badge';

export interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  tag?: string;
  tagBgColor?: string;
  content: React.ReactNode;
  note?: string;
  footer?: {
    text: string;
    href?: string;
  };
  className?: string;
}

export interface PopupData {
  title?: string | React.ReactNode;
  tag?: string;
  tagBgColor?: string;
  content: React.ReactNode;
  note?: string;
  footer?: {
    text: string;
    href?: string;
  };
  className?: string;
}

export default function Popup({
  isOpen,
  onClose,
  title,
  tag,
  tagBgColor,
  content,
  note,
  footer,
  className,
}: PopupProps) {
  const titleId = useId();
  const popupBoxClassName = className ? `${s.popup_box} ${className}` : s.popup_box;

  if (!isOpen) return null;

  return (
    <div className={s.popup}>
      <div className={s.popup_overlay} onClick={onClose} />
      <div
        className={popupBoxClassName}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        {title && (<div className={s.popup_header}>
          {tag && <Badge bgColor={tagBgColor} size='large' className={s.popup_tag}>{tag}</Badge>}
          <strong id={titleId} className={s.popup_title}>{title}</strong>
        </div>)}
        <div className={s.popup_content}>
          <div className={s.popup_body}>
            {content}
            {note && <small className={s.popup_note}>{note}</small>}
          </div>
          {footer && (
            <div className={s.popup_footer}>
              <div className={s.popup_footer_inner}>
                {footer.href ? (
                  <Button theme="text" href={footer.href} className={s.popup_footer_btn}>
                    {footer.text}
                    <Icon name={ArrowArrow} direction="right" size={16} />
                  </Button>
                ) : (
                  <span className={s.popup_footer_text}>{footer.text}</span>
                )}
              </div>
            </div>
          )}
        </div>
        <Button theme="icon" type='white' filled className={s.popup_close} onClick={onClose} aria-label="닫기">
          <Icon name={ActionCloseType1} size={16} className={s.popup_close_icon} />
        </Button>
      </div>
    </div>
  );
}
